import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Modal, Button } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import StarRatings from 'react-star-ratings';
import WineCard from './WineCard';
import 'rc-slider/assets/index.css';
import Slider from 'rc-slider';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { FiFilter, FiShoppingCart } from 'react-icons/fi';
import Cookies from 'js-cookie';

// Add keyframes for cart counter animation
const pulseAnimation = `
  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.1); }
    100% { transform: scale(1); }
  }
`;

// Inject the animation styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = pulseAnimation;
  document.head.appendChild(styleSheet);
}

function WineMarketplace() {
  const [wines, setWines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Filtros
  const [bodegaFiltro, setBodegaFiltro] = useState([]);
  const [cepaFiltro, setCepaFiltro] = useState([]);
  const [zonaFiltro, setZonaFiltro] = useState([]);
  const [soloVivino, setSoloVivino] = useState(false);
  const [precioMin, setPrecioMin] = useState(0);
  const [precioMax, setPrecioMax] = useState(0);
  const [precioRange, setPrecioRange] = useState([0, 0]);
  const [showFilterModal, setShowFilterModal] = useState(false);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedWine, setSelectedWine] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [showStockLimit, setShowStockLimit] = useState(false);

  const [isSticky, setIsSticky] = useState(false);
  const sentinelRef = useRef(null);

  const navigate = useNavigate();

  const [cartCount, setCartCount] = useState(0);
  const [maxAddable, setMaxAddable] = useState(null);
  const [showCartModal, setShowCartModal] = useState(false);
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [checkoutData, setCheckoutData] = useState({
    nombre: '',
    email: '',
    telefono: ''
  });
  const [discountCode, setDiscountCode] = useState('');
  const [discountApplied, setDiscountApplied] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountError, setDiscountError] = useState('');

  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);

  // Navigation dropdown states
  const [showCepasDropdown, setShowCepasDropdown] = useState(false);
  const [showRegionesDropdown, setShowRegionesDropdown] = useState(false);
  const [showBodegasDropdown, setShowBodegasDropdown] = useState(false);
  
  // Mobile detection hook
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Utilidad para mostrar el precio en formato ARS
  const getPrice = (value) => {
    if (!value) return '-';
    let val = String(value).replace(/\s/g, '');
    if (val.includes(',')) val = val.split(',')[0];
    val = val.replace(/\D/g, '');
    const num = Number(val);
    return !isNaN(num) && num > 0
      ? num.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
      : '-';
  };

  const openModal = (wine) => {
    // Leer cantidad ya en carrito
    const cart = Cookies.get('cart');
    let cartObj = {};
    if (cart) {
      try { cartObj = JSON.parse(cart); } catch (e) {}
    }
    const alreadyInCart = cartObj[wine.sku] || 0;
    const stock = Number(wine.stock) || 0;
    const max = Math.max(stock - alreadyInCart, 0);
    setMaxAddable(max);
    setSelectedWine(wine);
    setQuantity(max > 0 ? 1 : 0);
    setShowStockLimit(false); // Reset stock limit message
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedWine(null);
    setQuantity(1);
    setMaxAddable(null);
    setShowStockLimit(false); // Reset stock limit message
  };

  const handleAddToCart = () => {
    // Guardar en cookie
    const cart = Cookies.get('cart');
    let cartObj = {};
    if (cart) {
      try {
        cartObj = JSON.parse(cart);
      } catch (e) {}
    }
    const sku = selectedWine.sku;
    cartObj[sku] = (cartObj[sku] || 0) + quantity;
    Cookies.set('cart', JSON.stringify(cartObj), { expires: 7 });
    // Actualizar contador
    const total = Object.values(cartObj).reduce((sum, qty) => sum + qty, 0);
    setCartCount(total);
    closeModal();
  };

  const handleIncrease = () => {
    if (selectedWine && quantity < maxAddable) {
      setQuantity(quantity + 1);
      setShowStockLimit(false);
    } else {
      setShowStockLimit(true);
    }
  };

  const handleRemoveFromCart = (sku) => {
    const cart = Cookies.get('cart');
    if (!cart) return;
    let cartObj = {};
    try { cartObj = JSON.parse(cart); } catch (e) { return; }
    delete cartObj[sku];
    Cookies.set('cart', JSON.stringify(cartObj), { expires: 7 });
    const total = Object.values(cartObj).reduce((sum, qty) => sum + qty, 0);
    setCartCount(total);
    
    // Reset discount if cart becomes empty
    if (total === 0) {
      setDiscountCode('');
      setDiscountApplied(false);
      setDiscountAmount(0);
      setDiscountPercentage(0);
      setDiscountError('');
    }
  };

  const handleUpdateCartQuantity = (sku, newQty) => {
    const cart = Cookies.get('cart');
    if (!cart) return;
    let cartObj = {};
    try { cartObj = JSON.parse(cart); } catch (e) { return; }
    const wine = wines.find(w => w.sku === sku);
    if (!wine) return;
    const stock = Number(wine.stock) || 0;
    if (newQty > stock) return;
    cartObj[sku] = newQty;
    Cookies.set('cart', JSON.stringify(cartObj), { expires: 7 });
    const total = Object.values(cartObj).reduce((sum, qty) => sum + qty, 0);
    setCartCount(total);
    
    // Recalculate discount if applied
    if (discountApplied && discountPercentage > 0) {
      const cartTotal = Object.entries(cartObj).reduce((sum, [itemSku, qty]) => {
        const item = wines.find(w => w.sku === itemSku);
        if (!item) return sum;
        let val = String(item.cons_final).replace(/\s/g, '');
        if (val.includes(',')) val = val.split(',')[0];
        val = val.replace(/\D/g, '');
        const precio = Number(val);
        return sum + (isNaN(precio) ? 0 : precio * qty);
      }, 0);
      
      const newDiscountAmount = Math.round(cartTotal * (discountPercentage / 100));
      setDiscountAmount(newDiscountAmount);
    }
  };

  const validateDiscountCode = async (code) => {
    setDiscountLoading(true);
    setDiscountError('');
    
    try {
      const response = await axios.post('https://pampawineclub.com/api/validate_discount', {
        code: code.trim()
      });
      
      if (response.data && response.data.valid) {
        const cartTotal = getCartItems().reduce((sum, item) => {
          let val = String(item.cons_final).replace(/\s/g, '');
          if (val.includes(',')) val = val.split(',')[0];
          val = val.replace(/\D/g, '');
          const precio = Number(val);
          return sum + (isNaN(precio) ? 0 : precio * item.cantidad);
        }, 0);
        
        const discountAmount = Math.round(cartTotal * (response.data.discountPercentage / 100));
        
        setDiscountApplied(true);
        setDiscountAmount(discountAmount);
        setDiscountPercentage(response.data.discountPercentage);
        setDiscountError('');
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        setDiscountError(error.response.data.error);
      } else {
        setDiscountError('Error validando código de descuento');
      }
      setDiscountApplied(false);
      setDiscountAmount(0);
      setDiscountPercentage(0);
    } finally {
      setDiscountLoading(false);
    }
  };

  const handleCheckoutSubmit = async () => {
    setCheckoutLoading(true);
    setCheckoutError('');
    setCheckoutSuccess(false);
    // Validación de campos requeridos
    if (!checkoutData.nombre.trim() || !checkoutData.email.trim() || !checkoutData.telefono.trim()) {
      setCheckoutError('Por favor completa todos los campos.');
      setCheckoutLoading(false);
      return;
    }
    try {
      const productos = getCartItems().map(item => ({
        sku: item.sku,
        descripcion: item.descripcion,
        cantidad: item.cantidad,
        precioUnit: Number(String(item.cons_final).replace(/\D/g, ''))
      }));
      const subtotal = productos.reduce((sum, p) => sum + (p.precioUnit * p.cantidad), 0);
      const total = subtotal - discountAmount;
      
      const res = await axios.post('https://pampawineclub.com/api/marketplace_order', {
        nombre: checkoutData.nombre,
        email: checkoutData.email,
        telefono: checkoutData.telefono,
        productos,
        total,
        discountCode: discountApplied ? discountCode : null,
        discountAmount: discountApplied ? discountAmount : 0
      });
      if (res.data && res.data.ok) {
        setCheckoutSuccess(true);
        // Reset cart
        Cookies.remove('cart');
        setCartCount(0);
        // Reset discount state
        setDiscountCode('');
        setDiscountApplied(false);
        setDiscountAmount(0);
        setDiscountPercentage(0);
        setDiscountError('');
        // Reset checkout form
        setCheckoutData({
          nombre: '',
          email: '',
          telefono: ''
        });
        setTimeout(() => {
          setShowCheckoutForm(false);
          setShowCartModal(false);
          setCheckoutSuccess(false);
        }, 2000);
      } else {
        setCheckoutError(res.data && res.data.error ? res.data.error : 'No se pudo enviar el pedido. Intenta nuevamente.');
      }
    } catch (e) {
      if (e.response && e.response.data && e.response.data.error) {
        setCheckoutError(e.response.data.error);
      } else {
        setCheckoutError('No se pudo enviar el pedido. Intenta nuevamente.');
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  useEffect(() => {
    axios.get('https://pampawineclub.com/api/marketplace_wines')
      .then(response => {
        setWines(response.data);
        setLoading(false);

        // Inicializar rango de precios
        const precios = response.data.map(w => {
          let val = String(w.cons_final).replace(/\s/g, '');
          if (val.includes(',')) val = val.split(',')[0];
          val = val.replace(/\D/g, '');
          return Number(val);
        }).filter(n => !isNaN(n) && n > 0);
        const min = Math.min(...precios);
        const max = Math.max(...precios);
        setPrecioMin(min);
        setPrecioMax(max);
        setPrecioRange([min, max]);
      })
      .catch(error => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!sentinelRef.current) return;
      const rect = sentinelRef.current.getBoundingClientRect();
      setIsSticky(rect.top <= 0);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Leer carrito de cookie al cargar
  useEffect(() => {
    const cart = Cookies.get('cart');
    if (cart) {
      try {
        const parsed = JSON.parse(cart);
        const total = Object.values(parsed).reduce((sum, qty) => sum + qty, 0);
        setCartCount(total);
      } catch (e) {
        setCartCount(0);
      }
    }
  }, []);

  // Opciones únicas para los filtros con filtrado cruzado
  const getAllBodegas = () => Array.from(new Set(wines.map(w => w.bodega).filter(Boolean)));
  const getAllCepas = () => Array.from(new Set(wines.map(w => (w.cepa || '').trim()).filter(Boolean)));
  const getAllZonas = () => Array.from(new Set(wines.map(w => (w.provincia || '').trim()).filter(Boolean)));

  // Filtered options based on current selections
  const getFilteredBodegas = () => {
    let filteredWines = wines;
    
    // Filter by selected provinces first
    if (zonaFiltro.length > 0) {
      filteredWines = filteredWines.filter(w => zonaFiltro.includes((w.provincia || '').trim()));
    }
    
    // Filter by selected cepas
    if (cepaFiltro.length > 0) {
      filteredWines = filteredWines.filter(w => cepaFiltro.includes((w.cepa || '').trim()));
    }
    
    return Array.from(new Set(filteredWines.map(w => w.bodega).filter(Boolean)));
  };

  const getFilteredZonas = () => {
    let filteredWines = wines;
    
    // Filter by selected bodegas first
    if (bodegaFiltro.length > 0) {
      filteredWines = filteredWines.filter(w => bodegaFiltro.includes(w.bodega));
    }
    
    // Filter by selected cepas
    if (cepaFiltro.length > 0) {
      filteredWines = filteredWines.filter(w => cepaFiltro.includes((w.cepa || '').trim()));
    }
    
    return Array.from(new Set(filteredWines.map(w => (w.provincia || '').trim()).filter(Boolean)));
  };

  const getFilteredCepas = () => {
    let filteredWines = wines;
    
    // Filter by selected bodegas
    if (bodegaFiltro.length > 0) {
      filteredWines = filteredWines.filter(w => bodegaFiltro.includes(w.bodega));
    }
    
    // Filter by selected provinces
    if (zonaFiltro.length > 0) {
      filteredWines = filteredWines.filter(w => zonaFiltro.includes((w.provincia || '').trim()));
    }
    
    return Array.from(new Set(filteredWines.map(w => (w.cepa || '').trim()).filter(Boolean)));
  };

  // Get current filtered options
  const bodegas = getFilteredBodegas();
  const cepas = getFilteredCepas();
  const zonas = getFilteredZonas();

  // Auto-cleanup invalid selections when filters change
  React.useEffect(() => {
    const validBodegas = getFilteredBodegas();
    const invalidBodegas = bodegaFiltro.filter(b => !validBodegas.includes(b));
    if (invalidBodegas.length > 0) {
      setBodegaFiltro(bodegaFiltro.filter(b => validBodegas.includes(b)));
    }
  }, [zonaFiltro, cepaFiltro]);

  React.useEffect(() => {
    const validZonas = getFilteredZonas();
    const invalidZonas = zonaFiltro.filter(z => !validZonas.includes(z));
    if (invalidZonas.length > 0) {
      setZonaFiltro(zonaFiltro.filter(z => validZonas.includes(z)));
    }
  }, [bodegaFiltro, cepaFiltro]);

  React.useEffect(() => {
    const validCepas = getFilteredCepas();
    const invalidCepas = cepaFiltro.filter(c => !validCepas.includes(c));
    if (invalidCepas.length > 0) {
      setCepaFiltro(cepaFiltro.filter(c => validCepas.includes(c)));
    }
  }, [bodegaFiltro, zonaFiltro]);

  // Filtro de vinos
  const filteredWines = wines
    .filter(w => bodegaFiltro.length === 0 || bodegaFiltro.includes(w.bodega))
    .filter(w => {
      const match = cepaFiltro.length === 0 || cepaFiltro.includes((w.cepa || '').trim());
      if (!match) {
        console.log('No match cepa:', w.cepa, 'Filtro:', cepaFiltro);
      }
      return match;
    })
    .filter(w => {
      const match = zonaFiltro.length === 0 || zonaFiltro.includes((w.provincia || '').trim());
      if (!match) {
        console.log('No match zona:', w.provincia, 'Filtro:', zonaFiltro);
      }
      return match;
    })
    .filter(w => !soloVivino || (w.vivino && w.vivino !== ''))
    .filter(w => {
      let val = String(w.cons_final).replace(/\s/g, '');
      if (val.includes(',')) val = val.split(',')[0];
      val = val.replace(/\D/g, '');
      const num = Number(val);
      return (!precioRange[0] || num >= precioRange[0]) && (!precioRange[1] || num <= precioRange[1]);
    })
    .filter(wine =>
      wine.descripcion?.toLowerCase().includes(search.toLowerCase())
    );

  // Helper para renderizar estrellas
  const renderStars = (score) => {
    if (!score) return null;
    const numericScore = parseFloat(score);
    const fullStars = Math.floor(numericScore);
    const halfStar = numericScore % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (halfStar ? 1 : 0);
    return (
      <>
        {Array(fullStars).fill().map((_, i) => <span key={'f'+i} style={{color:'#a03a2b', fontSize:'1.2em'}}>★</span>)}
        {halfStar && <span style={{color:'#a03a2b', fontSize:'1.2em'}}>☆</span>}
        {Array(emptyStars).fill().map((_, i) => <span key={'e'+i} style={{color:'#ddd', fontSize:'1.2em'}}>★</span>)}
      </>
    );
  };

  // Calculate counts for provinces and cepas
  const calculateProvinciasCount = () => {
    const provincias = wines
      .map(w => w.provincia)
      .filter(provincia => provincia && provincia.trim() !== '');
    
    return new Set(provincias).size;
  };

  const calculateCepasCount = () => {
    const cepas = wines
      .map(w => w.cepa)
      .filter(cepa => cepa && cepa.trim() !== '');
    
    return new Set(cepas).size;
  };

  const provinciasCount = calculateProvinciasCount();
  const cepasCount = calculateCepasCount();

  console.log('cepaFiltro:', cepaFiltro, 'zonaFiltro:', zonaFiltro, 'ejemplo vino:', wines[0]);
  console.log('Opciones cepas:', cepas, 'Opciones zonas:', zonas);

  // Color de fondo de la cabecera
  const stickyBg = 'linear-gradient(90deg, #7b2e2e 0%, #b97a56 100%)';

  // Función para obtener los productos del carrito con info completa
  const getCartItems = () => {
    const cart = Cookies.get('cart');
    if (!cart) return [];
    let cartObj = {};
    try { cartObj = JSON.parse(cart); } catch (e) { return []; }
    // Cruzar con vinos
    return Object.entries(cartObj).map(([sku, qty]) => {
      const wine = wines.find(w => w.sku === sku);
      if (!wine) return null;
      return { ...wine, cantidad: qty };
    }).filter(Boolean);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8f9fa',
      padding: '0',
      margin: '0'
    }}>
      <div
        style={{
          background: '#ffffff',
          borderBottom: '1px solid #e5e5e5',
          boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
          position: 'sticky',
          top: 0,
          zIndex: 1000
        }}
      >
        {/* Top Header Bar */}
        <div style={{
          padding: '1rem',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <div style={{
            maxWidth: '1400px',
            margin: '0 auto'
          }}>
            {/* Desktop Layout */}
            <div style={{
              display: !isMobile ? 'flex' : 'none',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              {/* Logo */}
              <div 
                style={{
                  cursor: 'pointer',
                  transition: 'opacity 0.2s ease'
                }}
                onClick={() => navigate('/marketplace')}
                title="Ir al inicio"
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.8';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                <img
                  src="https://pampawineclub.com/static/media/logo_resize.c8df7e69807b5ff20171.png"
                  alt="Pampa Wine Club"
                  style={{
                    height: '80px',
                    width: 'auto'
                  }}
                />
              </div>

              {/* Search Bar */}
              <div style={{
                flex: 1,
                maxWidth: '500px',
                margin: '0 3rem',
                position: 'relative'
              }}>
                                  <svg 
                    style={{
                      position: 'absolute',
                      left: '1rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 1
                    }}
                    xmlns="http://www.w3.org/2000/svg" 
                    width="18" 
                    height="18" 
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#999" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8"/>
                    <path d="M21 21l-4.35-4.35"/>
                  </svg>
                <input
                  type="text"
                  placeholder="Search any wine"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.8rem 1rem 0.8rem 3rem',
                    borderRadius: '25px',
                    border: '1px solid #ddd',
                    fontSize: '16px',
                    outline: 'none',
                    background: '#f8f8f8',
                    color: '#333',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.border = '1px solid #a03a2b';
                    e.target.style.background = '#fff';
                  }}
                  onBlur={(e) => {
                    e.target.style.border = '1px solid #ddd';
                    e.target.style.background = '#f8f8f8';
                  }}
                />
              </div>

              {/* Right Side Actions */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1.5rem'
              }}>
                {/* Stats */}
                <div style={{
                  display: 'flex',
                  gap: '1.5rem',
                  fontSize: '0.9rem',
                  color: '#666'
                }}>
                  <span>{wines.length}+ Vinos</span>
                  <span>🗺️ {provinciasCount} Provincias</span>
                  <span>🍇 {cepasCount} Cepas</span>
                </div>

                {/* Filter & Cart */}
                <div style={{
                  display: 'flex',
                  gap: '0.5rem'
                }}>
                  <button
                    onClick={() => setShowFilterModal(true)}
                    style={{
                      background: 'none',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      padding: '0.5rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      color: '#666'
                    }}
                    title="Filtros"
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = '#a03a2b';
                      e.target.style.color = '#a03a2b';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = '#ddd';
                      e.target.style.color = '#666';
                    }}
                  >
                    <FiFilter size={18} />
                  </button>

                  <div style={{ position: 'relative' }}>
                    <button
                      style={{
                        background: cartCount > 0 ? '#a03a2b' : 'none',
                        border: `1px solid ${cartCount > 0 ? '#a03a2b' : '#ddd'}`,
                        borderRadius: '8px',
                        padding: '0.5rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        color: cartCount > 0 ? 'white' : '#666'
                      }}
                      title="Carrito"
                      onClick={() => setShowCartModal(true)}
                    >
                      <FiShoppingCart size={18} />
                      {cartCount > 0 && (
                        <span style={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          background: '#ff4444',
                          color: 'white',
                          borderRadius: '50%',
                          padding: '2px 6px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          minWidth: '20px',
                          textAlign: 'center'
                        }}>{cartCount}</span>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Layout */}
            <div style={{
              display: isMobile ? 'block' : 'none'
            }}>
              {/* Mobile Single Row: Logo + Search + Filter/Cart */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '1rem',
                height: '70px'
              }}>
                {/* Logo */}
                <div 
                  style={{
                    cursor: 'pointer',
                    transition: 'opacity 0.2s ease',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '70px',
                    width: '80px'
                  }}
                  onClick={() => navigate('/marketplace')}
                  title="Ir al inicio"
                >
                  <img
                    src="https://pampawineclub.com/static/media/logo_resize.c8df7e69807b5ff20171.png"
                    alt="Pampa Wine Club"
                    style={{
                      height: '68px',
                      width: 'auto'
                    }}
                  />
                </div>

                {/* Search Bar */}
                <div style={{
                  position: 'relative',
                  flex: '1 1 auto',
                  minWidth: '120px',
                  maxWidth: '280px',
                  height: '70px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <svg 
                    style={{
                      position: 'absolute',
                      left: '0.8rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 1
                    }}
                    xmlns="http://www.w3.org/2000/svg" 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#999" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8"/>
                    <path d="M21 21l-4.35-4.35"/>
                  </svg>
                                    <input
                    type="text"
                    placeholder="Buscar vino"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{
                      width: '100%',
                      height: '52px',
                      padding: '0 0.8rem 0 2.5rem',
                      borderRadius: '26px',
                      border: '1px solid #ddd',
                      fontSize: '16px',
                      outline: 'none',
                      background: '#f8f8f8',
                      color: '#333',
                      boxSizing: 'border-box',
                      margin: 0,
                      lineHeight: 'normal'
                    }}
                    onFocus={(e) => {
                      e.target.style.border = '1px solid #a03a2b';
                      e.target.style.background = '#fff';
                    }}
                    onBlur={(e) => {
                      e.target.style.border = '1px solid #ddd';
                      e.target.style.background = '#f8f8f8';
                    }}
                  />
                </div>

                {/* Filter & Cart */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                  flexShrink: 0,
                  height: '70px',
                  width: '110px'
                }}>
                  <button
                    onClick={() => setShowFilterModal(true)}
                    style={{
                      background: 'none',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      padding: 0,
                      cursor: 'pointer',
                      color: '#666',
                      height: '52px',
                      width: '52px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxSizing: 'border-box',
                      margin: 0
                    }}
                    title="Filtros"
                  >
                    <FiFilter size={20} />
                  </button>

                  <div style={{ position: 'relative' }}>
                    <button
                      style={{
                        background: cartCount > 0 ? '#a03a2b' : 'none',
                        border: `1px solid ${cartCount > 0 ? '#a03a2b' : '#ddd'}`,
                        borderRadius: '8px',
                        padding: 0,
                        cursor: 'pointer',
                        color: cartCount > 0 ? 'white' : '#666',
                        height: '52px',
                        width: '52px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxSizing: 'border-box',
                        margin: 0,
                        position: 'relative'
                      }}
                      title="Carrito"
                      onClick={() => setShowCartModal(true)}
                    >
                      <FiShoppingCart size={20} />
                      {cartCount > 0 && (
                        <span style={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          background: '#ff4444',
                          color: 'white',
                          borderRadius: '50%',
                          padding: '2px 6px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          minWidth: '20px',
                          textAlign: 'center'
                        }}>{cartCount}</span>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Mobile Stats */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '1rem',
                fontSize: '0.8rem',
                color: '#666'
              }}>
                <span>{wines.length}+ Vinos</span>
                <span>🗺️ {provinciasCount} Provincias</span>
                <span>🍇 {cepasCount} Cepas</span>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Menu */}
        <div style={{
          padding: isMobile ? '0.5rem 1rem' : '0.8rem 2rem'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: isMobile ? '1rem' : '2.5rem',
            maxWidth: '1400px',
            margin: '0 auto',
            overflowX: 'visible',
            paddingBottom: isMobile ? '0.5rem' : '0',
            whiteSpace: isMobile ? 'nowrap' : 'normal'
          }}>
                        {/* Bodegas Dropdown */}
            <div style={{ position: 'relative' }}>
              <div 
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: isMobile ? '0.4rem 0.8rem' : '0.5rem 1rem',
                  borderRadius: '8px',
                  color: bodegaFiltro.length > 0 ? '#a03a2b' : '#666',
                  backgroundColor: bodegaFiltro.length > 0 ? '#fdf2f0' : 'transparent',
                  fontSize: isMobile ? '0.8rem' : '0.9rem',
                  fontWeight: bodegaFiltro.length > 0 ? 600 : 500,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  flexShrink: 0
                }}
                onClick={() => {
                  setShowBodegasDropdown(!showBodegasDropdown);
                  setShowCepasDropdown(false);
                  setShowRegionesDropdown(false);
                }}
                onMouseEnter={(e) => !bodegaFiltro.length && (e.target.style.background = '#f0f0f0')}
                onMouseLeave={(e) => !bodegaFiltro.length && (e.target.style.background = 'transparent')}
              >
                🏭 Bodegas {bodegaFiltro.length > 0 && `(${bodegaFiltro.length})`}
                <span style={{ fontSize: '0.7rem', marginLeft: '0.2rem' }}>▼</span>
              </div>
              {showBodegasDropdown && (
                 <div style={{
                   position: isMobile ? 'absolute' : 'absolute',
                   top: isMobile ? '100%' : '100%',
                   left: isMobile ? '0' : 0,
                   right: isMobile ? 'auto' : 'auto',
                   transform: isMobile ? 'none' : 'none',
                   background: 'white',
                   border: '1px solid #e5e5e5',
                   borderRadius: '8px',
                   boxShadow: isMobile ? '0 4px 12px rgba(0,0,0,0.15)' : '0 4px 12px rgba(0,0,0,0.15)',
                   padding: '0.5rem 0',
                   minWidth: isMobile ? '200px' : '250px',
                   maxWidth: isMobile ? '280px' : 'none',
                   maxHeight: '300px',
                   overflowY: 'auto',
                   zIndex: 1001,
                   marginTop: '0.5rem'
                 }}>
                  
                  <div
                    style={{
                      padding: '0.5rem 1rem',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      color: bodegaFiltro.length === 0 ? '#a03a2b' : '#666',
                      backgroundColor: bodegaFiltro.length === 0 ? '#fdf2f0' : 'transparent',
                      fontWeight: bodegaFiltro.length === 0 ? 600 : 400,
                      borderBottom: '1px solid #f0f0f0',
                      marginBottom: '0.5rem'
                    }}
                    onClick={() => setBodegaFiltro([])}
                  >
                    {bodegaFiltro.length === 0 ? '✓ ' : ''}Todas las bodegas
                  </div>
                  {bodegas.sort().map(bodega => (
                    <div
                      key={bodega}
                      style={{
                        padding: '0.5rem 1rem',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        color: bodegaFiltro.includes(bodega) ? '#a03a2b' : '#666',
                        backgroundColor: bodegaFiltro.includes(bodega) ? '#fdf2f0' : 'transparent',
                        fontWeight: bodegaFiltro.includes(bodega) ? 600 : 400,
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => {
                        if (bodegaFiltro.includes(bodega)) {
                          setBodegaFiltro(bodegaFiltro.filter(b => b !== bodega));
                        } else {
                          setBodegaFiltro([...bodegaFiltro, bodega]);
                        }
                      }}
                      onMouseEnter={(e) => !bodegaFiltro.includes(bodega) && (e.target.style.background = '#f0f0f0')}
                      onMouseLeave={(e) => !bodegaFiltro.includes(bodega) && (e.target.style.background = 'transparent')}
                    >
                      {bodegaFiltro.includes(bodega) ? '✓ ' : ''}{bodega}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Regiones Dropdown */}
            <div style={{ position: 'relative' }}>
              <div 
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: isMobile ? '0.4rem 0.8rem' : '0.5rem 1rem',
                  borderRadius: '8px',
                  color: zonaFiltro.length > 0 ? '#a03a2b' : '#666',
                  backgroundColor: zonaFiltro.length > 0 ? '#fdf2f0' : 'transparent',
                  fontSize: isMobile ? '0.8rem' : '0.9rem',
                  fontWeight: zonaFiltro.length > 0 ? 600 : 500,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  flexShrink: 0
                }}
                onClick={() => {
                  setShowRegionesDropdown(!showRegionesDropdown);
                  setShowCepasDropdown(false);
                  setShowBodegasDropdown(false);
                }}
                onMouseEnter={(e) => !zonaFiltro.length && (e.target.style.background = '#f0f0f0')}
                onMouseLeave={(e) => !zonaFiltro.length && (e.target.style.background = 'transparent')}
              >
                📍 Provincias {zonaFiltro.length > 0 && `(${zonaFiltro.length})`}
                <span style={{ fontSize: '0.7rem', marginLeft: '0.2rem' }}>▼</span>
              </div>
                             {showRegionesDropdown && (
                 <div style={{
                   position: isMobile ? 'absolute' : 'absolute',
                   top: isMobile ? '100%' : '100%',
                   left: isMobile ? 'auto' : 0,
                   right: isMobile ? '0' : 'auto',
                   transform: isMobile ? 'none' : 'none',
                   background: 'white',
                   border: '1px solid #e5e5e5',
                   borderRadius: '8px',
                   boxShadow: isMobile ? '0 4px 12px rgba(0,0,0,0.15)' : '0 4px 12px rgba(0,0,0,0.15)',
                   padding: '0.5rem 0',
                   minWidth: isMobile ? '200px' : '200px',
                   maxWidth: isMobile ? '280px' : 'none',
                   maxHeight: '300px',
                   overflowY: 'auto',
                   zIndex: 1001,
                   marginTop: '0.5rem'
                 }}>
                  
                  <div
                    style={{
                      padding: '0.5rem 1rem',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      color: zonaFiltro.length === 0 ? '#a03a2b' : '#666',
                      backgroundColor: zonaFiltro.length === 0 ? '#fdf2f0' : 'transparent',
                      fontWeight: zonaFiltro.length === 0 ? 600 : 400,
                      borderBottom: '1px solid #f0f0f0',
                      marginBottom: '0.5rem'
                    }}
                    onClick={() => setZonaFiltro([])}
                  >
                    {zonaFiltro.length === 0 ? '✓ ' : ''}Todas las provincias
                  </div>
                  
                  {zonas.sort().map(zona => (
                    <div
                      key={zona}
                      style={{
                        padding: '0.5rem 1rem',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        color: zonaFiltro.includes(zona) ? '#a03a2b' : '#666',
                        backgroundColor: zonaFiltro.includes(zona) ? '#fdf2f0' : 'transparent',
                        fontWeight: zonaFiltro.includes(zona) ? 600 : 400,
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => {
                        if (zonaFiltro.includes(zona)) {
                          setZonaFiltro(zonaFiltro.filter(z => z !== zona));
                        } else {
                          setZonaFiltro([...zonaFiltro, zona]);
                        }
                      }}
                      onMouseEnter={(e) => !zonaFiltro.includes(zona) && (e.target.style.background = '#f0f0f0')}
                      onMouseLeave={(e) => !zonaFiltro.includes(zona) && (e.target.style.background = 'transparent')}
                    >
                      {zonaFiltro.includes(zona) ? '✓ ' : ''}{zona}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Cepas Dropdown */}
            <div style={{ position: 'relative' }}>
              <div 
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: isMobile ? '0.4rem 0.8rem' : '0.5rem 1rem',
                  borderRadius: '8px',
                  color: cepaFiltro.length > 0 ? '#a03a2b' : '#666',
                  backgroundColor: cepaFiltro.length > 0 ? '#fdf2f0' : 'transparent',
                  fontSize: isMobile ? '0.8rem' : '0.9rem',
                  fontWeight: cepaFiltro.length > 0 ? 600 : 500,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  flexShrink: 0
                }}
                onClick={() => {
                  setShowCepasDropdown(!showCepasDropdown);
                  setShowRegionesDropdown(false);
                  setShowBodegasDropdown(false);
                }}
                onMouseEnter={(e) => !cepaFiltro.length && (e.target.style.background = '#f0f0f0')}
                onMouseLeave={(e) => !cepaFiltro.length && (e.target.style.background = 'transparent')}
              >
                🍇 Cepas {cepaFiltro.length > 0 && `(${cepaFiltro.length})`}
                <span style={{ fontSize: '0.7rem', marginLeft: '0.2rem' }}>▼</span>
              </div>
              {showCepasDropdown && (
                <div style={{
                  position: isMobile ? 'absolute' : 'absolute',
                  top: isMobile ? '100%' : '100%',
                  left: isMobile ? 'auto' : 0,
                  right: isMobile ? '0' : 'auto',
                  transform: isMobile ? 'none' : 'none',
                  background: 'white',
                  border: '1px solid #e5e5e5',
                  borderRadius: '8px',
                  boxShadow: isMobile ? '0 4px 12px rgba(0,0,0,0.15)' : '0 4px 12px rgba(0,0,0,0.15)',
                  padding: '0.5rem 0',
                  minWidth: isMobile ? '200px' : '200px',
                  maxWidth: isMobile ? '280px' : 'none',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  zIndex: 1001,
                  marginTop: '0.5rem'
                }}>
                  
                  <div
                    style={{
                      padding: '0.5rem 1rem',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      color: cepaFiltro.length === 0 ? '#a03a2b' : '#666',
                      backgroundColor: cepaFiltro.length === 0 ? '#fdf2f0' : 'transparent',
                      fontWeight: cepaFiltro.length === 0 ? 600 : 400,
                      borderBottom: '1px solid #f0f0f0',
                      marginBottom: '0.5rem'
                    }}
                    onClick={() => setCepaFiltro([])}
                  >
                    {cepaFiltro.length === 0 ? '✓ ' : ''}Todas las cepas
                  </div>
                  
                  {cepas.sort().map(cepa => (
                    <div
                      key={cepa}
                      style={{
                        padding: '0.5rem 1rem',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        color: cepaFiltro.includes(cepa) ? '#a03a2b' : '#666',
                        backgroundColor: cepaFiltro.includes(cepa) ? '#fdf2f0' : 'transparent',
                        fontWeight: cepaFiltro.includes(cepa) ? 600 : 400,
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => {
                        if (cepaFiltro.includes(cepa)) {
                          setCepaFiltro(cepaFiltro.filter(c => c !== cepa));
                        } else {
                          setCepaFiltro([...cepaFiltro, cepa]);
                        }
                      }}
                      onMouseEnter={(e) => !cepaFiltro.includes(cepa) && (e.target.style.background = '#f0f0f0')}
                      onMouseLeave={(e) => !cepaFiltro.includes(cepa) && (e.target.style.background = 'transparent')}
                    >
                      {cepaFiltro.includes(cepa) ? '✓ ' : ''}{cepa}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Click outside to close dropdowns - for both mobile and desktop */}
      {(showCepasDropdown || showRegionesDropdown || showBodegasDropdown) && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999
          }}
          onClick={() => {
            setShowCepasDropdown(false);
            setShowRegionesDropdown(false);
            setShowBodegasDropdown(false);
          }}
        />
      )}

      <div style={{ marginBottom: '2rem' }} />

      {/* Modal de filtros */}
      {showFilterModal && !loading && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.25)',
          zIndex: 1000,
          display: 'flex',
          alignItems: isMobile ? 'flex-start' : 'center',
          justifyContent: 'center',
          padding: isMobile ? '1.5rem 1rem 0.2rem 1rem' : '1rem 1rem 0.25rem 1rem'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 18,
            padding: isMobile ? '1rem' : '2rem',
            width: isMobile ? '100%' : '500px',
            height: isMobile ? 'auto' : 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <button
              onClick={() => setShowFilterModal(false)}
              style={{
                position: 'absolute',
                top: isMobile ? 12 : 12,
                right: isMobile ? 12 : 12,
                background: 'none',
                border: 'none',
                fontSize: isMobile ? 24 : 22,
                cursor: 'pointer',
                zIndex: 10,
                color: '#666',
                padding: isMobile ? '8px' : '4px',
                width: isMobile ? '40px' : 'auto',
                height: isMobile ? '40px' : 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Cerrar"
            >✕</button>
            <h3 style={{ marginBottom: isMobile ? 12 : 16, color: '#a03a2b', textAlign: 'center', fontWeight: 700, letterSpacing: 1, fontSize: isMobile ? '1.2rem' : '1.5rem' }}>Filtros</h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 8 : 12, flex: 'none', overflowY: 'visible' }}>
                              <label style={{ fontWeight: 600, color: '#a03a2b', marginBottom: 4, fontSize: isMobile ? 13 : 14 }}>Bodega</label>
                              <Select
                  isMulti
                  options={getAllBodegas().map(b => ({ value: b, label: b }))}
                  value={bodegaFiltro.map(b => ({ value: b, label: b }))}
                  onChange={selected => setBodegaFiltro(selected ? selected.map(opt => opt.value) : [])}
                  placeholder="Selecciona una o más bodegas"
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: '#a03a2b',
                      minHeight: isMobile ? 32 : 36,
                      fontSize: isMobile ? 13 : 14
                    }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 3000,
                    position: 'relative'
                  }),
                  option: (base, state) => ({
                    ...base,
                    color: state.isSelected ? '#fff' : '#a03a2b',
                    backgroundColor: state.isSelected ? '#a03a2b' : '#fff',
                    fontSize: 14,
                    padding: '6px 12px'
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: '#f8e6e0',
                    fontSize: 12
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: '#a03a2b',
                    fontSize: 12
                  })
                }}
              />
              <label style={{ fontWeight: 600, color: '#a03a2b', marginBottom: 4, fontSize: isMobile ? 13 : 14 }}>Provincia</label>
              <Select
                isMulti
                options={getAllZonas().map(z => ({ value: z, label: z }))}
                value={zonaFiltro.map(z => ({ value: z, label: z }))}
                onChange={selected => setZonaFiltro(selected ? selected.map(opt => opt.value) : [])}
                placeholder="Selecciona una o más provincias"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: '#a03a2b',
                    minHeight: isMobile ? 32 : 36,
                    fontSize: isMobile ? 13 : 14
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 3000,
                    position: 'relative'
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: '#f8e6e0',
                    fontSize: 12
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: '#a03a2b',
                    fontSize: 12
                  }),
                  option: (base, state) => ({
                    ...base,
                    color: state.isSelected ? '#fff' : '#a03a2b',
                    backgroundColor: state.isSelected ? '#a03a2b' : '#fff',
                    fontSize: 14,
                    padding: '6px 12px'
                  })
                }}
              />
              <label style={{ fontWeight: 600, color: '#a03a2b', marginBottom: 4, fontSize: isMobile ? 13 : 14 }}>Cepa</label>
              <Select
                isMulti
                options={getAllCepas().map(c => ({ value: c, label: c }))}
                value={cepaFiltro.map(c => ({ value: c, label: c }))}
                onChange={selected => setCepaFiltro(selected ? selected.map(opt => opt.value) : [])}
                placeholder="Selecciona una o más cepas"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: '#a03a2b',
                    minHeight: isMobile ? 32 : 36,
                    fontSize: isMobile ? 13 : 14
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 3000,
                    position: 'relative'
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: '#f8e6e0',
                    fontSize: 12
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: '#a03a2b',
                    fontSize: 12
                  }),
                  option: (base, state) => ({
                    ...base,
                    color: state.isSelected ? '#fff' : '#a03a2b',
                    backgroundColor: state.isSelected ? '#a03a2b' : '#fff',
                    fontSize: 14,
                    padding: '6px 12px'
                  })
                }}
              />

              <hr style={{ margin: isMobile ? '0.6rem 0' : '0.8rem 0', border: 0, borderTop: '1px solid #eee' }} />

              <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12, margin: isMobile ? '6px 0' : '8px 0' }}>
                <span style={{
                  fontWeight: 600,
                  color: soloVivino ? '#a03a2b' : '#888',
                  opacity: soloVivino ? 1 : 0.7,
                  transition: 'color 0.2s, opacity 0.2s'
                }}>
                  Solo calificados por Vivino
                </span>
                <button
                  type="button"
                  onClick={() => setSoloVivino(v => !v)}
                  style={{
                    width: 48,
                    height: 28,
                    borderRadius: 16,
                    border: `2px solid ${soloVivino ? '#a03a2b' : '#bbb'}`,
                    background: soloVivino ? '#a03a2b' : '#eee',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'background 0.2s, border 0.2s',
                    outline: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    padding: 0
                  }}
                  aria-pressed={soloVivino}
                >
                  <div style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    background: soloVivino ? '#fff' : '#bbb',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                    position: 'absolute',
                    left: soloVivino ? 22 : 2,
                    top: 2,
                    transition: 'left 0.2s, background 0.2s'
                  }} />
                </button>
              </div>

              {/* Slider de precio mejorado */}
              <div style={{ marginTop: isMobile ? 8 : 12 }}>
                <label style={{ fontWeight: 600, color: '#a03a2b', marginBottom: 4, fontSize: isMobile ? 13 : 14 }}>Rango de precio</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 14, color: '#555', minWidth: 60, textAlign: 'right' }}>ARS {precioRange[0]}</span>
                  <div style={{ flex: 1, padding: '0 8px' }}>
                    <Slider
                      range
                      min={precioMin}
                      max={precioMax}
                      value={precioRange}
                      onChange={setPrecioRange}
                      allowCross={false}
                      style={{ height: 28 }}
                      trackStyle={[{ backgroundColor: '#a03a2b', height: 8 }]}
                      handleStyle={[
                        { borderColor: '#a03a2b', backgroundColor: '#fff', height: 24, width: 24, marginTop: -8 },
                        { borderColor: '#a03a2b', backgroundColor: '#fff', height: 24, width: 24, marginTop: -8 }
                      ]}
                      railStyle={{ backgroundColor: '#eee', height: 8 }}
                    />
                  </div>
                  <span style={{ fontSize: 14, color: '#555', minWidth: 60 }}>ARS {precioRange[1]}</span>
                </div>
              </div>
              <button
                onClick={() => setShowFilterModal(false)}
                style={{
                  marginTop: isMobile ? 8 : 12,
                  background: '#a03a2b',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: isMobile ? '0.5rem 0.8rem' : '0.6rem 1rem',
                  fontWeight: 700,
                  fontSize: isMobile ? '0.9rem' : '1rem',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(113,131,142,0.07)'
                }}
              >
                Aplicar filtros
              </button>
              <button
                onClick={() => {
                  setCepaFiltro([]);
                  setZonaFiltro([]);
                  setBodegaFiltro([]);
                  setSoloVivino(false);
                  setPrecioRange([precioMin, precioMax]);
                  setShowFilterModal(false);
                }}
                style={{
                  marginTop: isMobile ? 6 : 8,
                  marginBottom: 4,
                  background: '#fff',
                  color: '#a03a2b',
                  border: '1.5px solid #a03a2b',
                  borderRadius: 8,
                  padding: isMobile ? '0.5rem 0.8rem' : '0.6rem 1rem',
                  fontWeight: 700,
                  fontSize: isMobile ? '0.9rem' : '1rem',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(113,131,142,0.07)'
                }}
              >
                Resetear filtros
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', color: '#71838E', fontSize: '1.2rem' }}>Cargando vinos...</div>
      ) : (
        <div className="row" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 2rem 2rem 2rem' }}>
          {filteredWines.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#71838E', fontSize: '1.1rem' }}>
              No se encontraron vinos.
            </div>
          ) : (
            filteredWines.map((wine, idx) => {
              const outOfStock = !wine.stock || Number(wine.stock) <= 0;
              return (
                <div className="col-12 col-lg-6 d-flex" key={idx} style={{ marginBottom: '2rem' }}>
                  <WineCard
                    wine={wine}
                    idx={idx}
                    outOfStock={outOfStock}
                    onAddToCart={openModal}
                  />
                </div>
              );
            })
          )}
        </div>
      )}

      {showModal && selectedWine && (
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.45)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 18,
              padding: '2.5rem 2.5rem 2rem 2.5rem',
              minWidth: 320,
              maxWidth: 400,
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <button
              onClick={closeModal}
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                background: 'none',
                border: 'none',
                fontSize: 22,
                cursor: 'pointer'
              }}
              title="Cerrar"
            >✕</button>
            <h3 style={{
              marginBottom: 18,
              color: '#a03a2b',
              textAlign: 'center',
              fontWeight: 700,
              letterSpacing: 1
            }}>
              Seleccionar cantidad
            </h3>
            <img
                              src={selectedWine.imagen ? selectedWine.imagen : "https://pampawineclub.com/img/pw_default.png"}
              alt={selectedWine.descripcion}
              style={{
                width: 70,
                height: 70,
                objectFit: 'contain',
                borderRadius: 10,
                background: '#fff',
                boxShadow: '0 2px 8px rgba(113,131,142,0.10)',
                marginBottom: 10
              }}
            />
            <div style={{ fontWeight: 600, fontSize: 18, color: '#2d2b27', marginBottom: 2 }}>
              {selectedWine.descripcion}
            </div>
            <div style={{ fontSize: 14, color: '#a03a2b', marginBottom: 18 }}>
              Stock disponible: <b>{selectedWine.stock}</b>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '2rem',
              margin: '1.2rem 0 0.5rem 0'
            }}>
              <button
                style={{
                  background: '#fff',
                  border: '2px solid #a03a2b',
                  color: '#a03a2b',
                  borderRadius: '50%',
                  width: 44,
                  height: 44,
                  fontSize: 28,
                  fontWeight: 700,
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: quantity > 1 ? 'pointer' : 'not-allowed'
                }}
                onClick={() => {
                  if (quantity > 1) {
                    setQuantity(quantity - 1);
                    setShowStockLimit(false);
                  }
                }}
                disabled={quantity <= 1}
              >-</button>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minWidth: 40
              }}>
                <span style={{
                  fontSize: 28,
                  textAlign: 'center',
                  fontWeight: 700,
                  color: '#a03a2b'
                }}>{quantity}</span>
                {showStockLimit && (
                  <div style={{ color: 'red', marginTop: '0.3rem', fontWeight: 'bold', fontSize: 13, textAlign: 'center', whiteSpace: 'nowrap' }}>
                    {maxAddable === 0 ? 'No hay más stock disponible' : `Solo ${maxAddable} disponible${maxAddable !== 1 ? 's' : ''}`}
                  </div>
                )}
              </div>
              <button
                style={{
                  background: '#fff',
                  border: '2px solid #a03a2b',
                  color: '#a03a2b',
                  borderRadius: '50%',
                  width: 44,
                  height: 44,
                  fontSize: 28,
                  fontWeight: 700,
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
                onClick={handleIncrease}
              >+</button>
            </div>
            {maxAddable === 0 && (
              <div style={{ color: 'red', marginTop: '0.5rem', fontWeight: 'bold', fontSize: 15 }}>
                ¡Ya tienes todo el stock disponible de este producto en el carrito!
              </div>
            )}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 18,
              marginTop: 28,
              width: '100%'
            }}>
              <button
                style={{
                  background: '#fff',
                  color: '#a03a2b',
                  border: '1.5px solid #a03a2b',
                  borderRadius: 8,
                  padding: '0.7rem 1.2rem',
                  fontWeight: 700,
                  fontSize: '1rem',
                  minWidth: 120,
                  textAlign: 'center',
                  cursor: 'pointer'
                }}
                onClick={closeModal}
              >
                CANCELAR
              </button>
              <button
                style={{
                  background: '#a03a2b',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '0.7rem 1.2rem',
                  fontWeight: 700,
                  fontSize: '1rem',
                  minWidth: 120,
                  textAlign: 'center',
                  cursor: maxAddable === 0 ? 'not-allowed' : 'pointer'
                }}
                onClick={handleAddToCart}
                disabled={maxAddable === 0}
              >
                AGREGAR
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de carrito */}
      {showCartModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.45)',
          zIndex: 2100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 18,
            padding: isMobile ? '1.5rem 1.5rem 1rem 1.5rem' : '2.5rem 2.5rem 2rem 2.5rem',
            minWidth: isMobile ? '90vw' : 340,
            maxWidth: isMobile ? '95vw' : 480,
            width: isMobile ? '90vw' : 'auto',
            maxHeight: isMobile ? '85vh' : '80vh',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            overflow: 'hidden'
          }}>
            <button
              onClick={() => {
                setShowCartModal(false);
                setShowCheckoutForm(false);
              }}
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                background: 'none',
                border: 'none',
                fontSize: 22,
                cursor: 'pointer'
              }}
              title="Cerrar"
            >✕</button>

            <h3 style={{ marginBottom: 22, color: '#a03a2b', textAlign: 'center', fontWeight: 700, letterSpacing: 1 }}>
              {showCheckoutForm ? 'Finalizar Pedido' : 'Carrito'}
            </h3>

            {!showCheckoutForm ? (
              <>
                <div style={{ 
                  width: '100%', 
                  maxHeight: isMobile ? '50vh' : 320, 
                  overflowY: 'auto', 
                  marginBottom: 18,
                  paddingRight: isMobile ? '0.5rem' : '0'
                }}>
                  {getCartItems().length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#888', fontSize: 17, margin: '2rem 0' }}>El carrito está vacío.</div>
                  ) : (
                    getCartItems().map((item) => (
                      <div key={item.sku} style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 18, borderBottom: '1px solid #eee', paddingBottom: 10 }}>
                        <img src={item.imagen ? item.imagen : "https://pampawineclub.com/img/pw_default.png"} alt={item.descripcion} style={{ width: 54, height: 54, objectFit: 'contain', borderRadius: 8, background: '#faf7f0', boxShadow: '0 1px 6px rgba(160,58,43,0.07)' }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, color: '#2d2b27', fontSize: 16 }}>{item.descripcion}</div>
                          <div style={{ color: '#a03a2b', fontSize: 15 }}>{item.bodega}</div>
                          <div style={{ color: '#888', fontSize: 14 }}>ARS {getPrice(item.cons_final)} x {item.cantidad}</div>
                        </div>
                        <div style={{ fontWeight: 700, color: '#a03a2b', fontSize: 17, minWidth: 70, textAlign: 'right' }}>
                          ARS {(Number(getPrice(item.cons_final).replace(/\./g, '')) * item.cantidad).toLocaleString('es-AR')}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                          <button
                            onClick={() => handleUpdateCartQuantity(item.sku, item.cantidad + 1)}
                            disabled={item.cantidad >= Number(item.stock)}
                            style={{
                              background: '#fff',
                              border: '1.5px solid #a03a2b',
                              borderRadius: '50%',
                              width: 28,
                              height: 28,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 18,
                              cursor: item.cantidad < Number(item.stock) ? 'pointer' : 'not-allowed',
                              color: '#a03a2b'
                            }}
                          >+</button>
                          <button
                            onClick={() => handleUpdateCartQuantity(item.sku, item.cantidad - 1)}
                            disabled={item.cantidad <= 1}
                            style={{
                              background: '#fff',
                              border: '1.5px solid #a03a2b',
                              borderRadius: '50%',
                              width: 28,
                              height: 28,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 18,
                              cursor: item.cantidad > 1 ? 'pointer' : 'not-allowed',
                              color: '#a03a2b'
                            }}
                          >-</button>
                          <button
                            onClick={() => handleRemoveFromCart(item.sku)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#e57373',
                              cursor: 'pointer',
                              fontSize: 18
                            }}
                          >✕</button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                {getCartItems().length > 0 && (
                  <>
                    {/* Discount Code Section */}
                    <div style={{ width: '100%', marginTop: 16, paddingTop: 16, borderTop: '1px solid #eee' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <input
                          type="text"
                          placeholder="Código de descuento"
                          value={discountCode}
                          onChange={(e) => setDiscountCode(e.target.value)}
                          style={{
                            flex: 1,
                            padding: '0.5rem 0.8rem',
                            borderRadius: 6,
                            border: '1px solid #ddd',
                            fontSize: isMobile ? '16px' : '0.9rem',
                            outline: 'none'
                          }}
                          disabled={discountApplied}
                        />
                        <button
                          onClick={() => {
                            if (discountCode.trim() && !discountApplied) {
                              validateDiscountCode(discountCode);
                            }
                          }}
                          disabled={!discountCode.trim() || discountApplied || discountLoading}
                          style={{
                            background: discountApplied ? '#28a745' : '#a03a2b',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            padding: '0.5rem 0.8rem',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            cursor: discountCode.trim() && !discountApplied && !discountLoading ? 'pointer' : 'not-allowed',
                            opacity: discountCode.trim() && !discountApplied && !discountLoading ? 1 : 0.6
                          }}
                        >
                          {discountLoading ? 'Validando...' : discountApplied ? '✓ Aplicado' : 'Aplicar'}
                        </button>
                      </div>
                      {discountError && (
                        <div style={{ 
                          padding: '0.5rem',
                          background: '#f8d7da',
                          borderRadius: 6,
                          fontSize: '0.8rem',
                          color: '#721c24',
                          fontWeight: 500,
                          marginTop: '0.5rem'
                        }}>
                          {discountError}
                        </div>
                      )}
                      {discountApplied && (
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          padding: '0.5rem',
                          background: '#d4edda',
                          borderRadius: 6,
                          fontSize: '0.9rem',
                          color: '#155724',
                          fontWeight: 600
                        }}>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span>Descuento aplicado ({discountPercentage}%)</span>
                            <span style={{ fontSize: '0.7rem', fontWeight: 400 }}>Código: {discountCode}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span>-ARS {discountAmount.toLocaleString('es-AR')}</span>
                            <button
                              onClick={() => {
                                setDiscountCode('');
                                setDiscountApplied(false);
                                setDiscountAmount(0);
                                setDiscountPercentage(0);
                                setDiscountError('');
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#dc3545',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                padding: '2px 4px'
                              }}
                              title="Remover descuento"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div style={{ width: '100%', borderTop: '1.5px solid #b89d2a', paddingTop: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700, fontSize: 19, color: '#a03a2b' }}>
                      <span>Total</span>
                      <span>
                        ARS {(getCartItems().reduce((sum, item) => {
                          let val = String(item.cons_final).replace(/\s/g, '');
                          if (val.includes(',')) val = val.split(',')[0];
                          val = val.replace(/\D/g, '');
                          const precio = Number(val);
                          return sum + (isNaN(precio) ? 0 : precio * item.cantidad);
                        }, 0) - discountAmount).toLocaleString('es-AR')}
                      </span>
                    </div>
                    
                    {/* Payment Methods Section */}
                    <div style={{ width: '100%', marginTop: 12, paddingTop: 12, borderTop: '1px solid #eee' }}>
                      <div style={{ marginBottom: 8, fontWeight: 600, color: '#2d2b27', fontSize: '0.8rem', textAlign: 'center' }}>
                        Métodos de Pago Aceptados
                      </div>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '12px',
                        padding: '8px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        border: '1px solid #e9ecef',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {/* MercadoPago Logo */}
                        <img 
                          src="https://pampawineclub.com/static/media/mp_logo.png"
                          alt="MercadoPago"
                          style={{
                            width: '50px',
                            height: 'auto',
                            objectFit: 'contain'
                          }}
                        />
                        
                        {/* Efectivo */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '0.7rem',
                          color: '#495057'
                        }}>
                          <span style={{ fontSize: '0.8rem' }}>💵</span>
                          <span><strong>Efectivo</strong></span>
                        </div>
                        
                        {/* Transferencia */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '0.7rem',
                          color: '#495057'
                        }}>
                          <span style={{ fontSize: '0.8rem' }}>🏦</span>
                          <span><strong>Transferencia</strong></span>
                        </div>
                      </div>
                    </div>
                    
                    <button
                      style={{
                        marginTop: 20,
                        background: '#a03a2b',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 8,
                        padding: '0.7rem 1.2rem',
                        fontWeight: 700,
                        fontSize: '1.1rem',
                        cursor: 'pointer',
                        boxShadow: '0 2px 8px rgba(113,131,142,0.07)'
                      }}
                      onClick={() => {
                        setShowCheckoutForm(true);
                        // Keep discount applied when going to checkout
                      }}
                    >
                      Realizar Pedido
                    </button>
                  </>
                )}
              </>
            ) : (
              <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ marginBottom: 20, width: '100%' }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#2d2b27' }}>Nombre</label>
                  <input
                    type="text"
                    value={checkoutData.nombre}
                    onChange={(e) => setCheckoutData({...checkoutData, nombre: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '0.7rem 1rem',
                      borderRadius: 8,
                      border: '1.5px solid #a03a2b',
                      fontSize: '1rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    placeholder="Ingrese su nombre"
                  />
                </div>
                <div style={{ marginBottom: 20, width: '100%' }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#2d2b27' }}>Correo Electrónico</label>
                  <input
                    type="email"
                    value={checkoutData.email}
                    onChange={(e) => setCheckoutData({...checkoutData, email: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '0.7rem 1rem',
                      borderRadius: 8,
                      border: '1.5px solid #a03a2b',
                      fontSize: '1rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    placeholder="Ingrese su correo electrónico"
                  />
                </div>
                <div style={{ marginBottom: 20, width: '100%' }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#2d2b27' }}>Teléfono</label>
                  <input
                    type="tel"
                    value={checkoutData.telefono}
                    onChange={(e) => setCheckoutData({...checkoutData, telefono: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '0.7rem 1rem',
                      borderRadius: 8,
                      border: '1.5px solid #a03a2b',
                      fontSize: '1rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    placeholder="Ingrese su teléfono"
                  />
                </div>
                <div style={{ display: 'flex', gap: 12, marginTop: 10, justifyContent: 'center', width: '100%' }}>
                  <button
                    onClick={() => setShowCheckoutForm(false)}
                    style={{
                      flex: 1,
                      background: '#fff',
                      color: '#a03a2b',
                      border: '1.5px solid #a03a2b',
                      borderRadius: 8,
                      padding: '0.7rem 1.2rem',
                      fontWeight: 700,
                      fontSize: '1rem',
                      cursor: 'pointer',
                      maxWidth: 180
                    }}
                  >
                    Volver
                  </button>
                  {checkoutSuccess && (
                    <div style={{ color: 'green', fontWeight: 700, marginBottom: 16, textAlign: 'center' }}>
                      ¡Pedido enviado con éxito. Por favor revise su correo electrónico para más información!
                    </div>
                  )}
                  {checkoutError && (
                    <div style={{ color: 'red', fontWeight: 700, marginBottom: 16, textAlign: 'center' }}>
                      {checkoutError}
                    </div>
                  )}
                  <button
                    onClick={handleCheckoutSubmit}
                    style={{
                      flex: 1,
                      background: '#a03a2b',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      padding: '0.7rem 1.2rem',
                      fontWeight: 700,
                      fontSize: '1rem',
                      cursor: checkoutLoading ? 'not-allowed' : 'pointer',
                      maxWidth: 180
                    }}
                    disabled={checkoutLoading}
                  >
                    {checkoutLoading ? 'Enviando...' : 'Finalizar Pedido'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sticky WhatsApp Button */}
      <a
        href="https://wa.me/5491153852002?text=Hola!%20Tengo%20una%20consulta%20sobre%20el%20marketplace%20de%20vinos"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: '60px',
          height: '60px',
          backgroundColor: '#25D366',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(37, 211, 102, 0.3)',
          zIndex: 1000,
          textDecoration: 'none',
          transition: 'all 0.3s ease',
          cursor: 'pointer'
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = 'scale(1.1)';
          e.target.style.boxShadow = '0 6px 20px rgba(37, 211, 102, 0.4)';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'scale(1)';
          e.target.style.boxShadow = '0 4px 12px rgba(37, 211, 102, 0.3)';
        }}
        title="Contactanos por WhatsApp"
      >
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.893 3.488"
            fill="white"
          />
        </svg>
      </a>

      {/* Footer */}
      <footer style={{
        background: 'linear-gradient(135deg, #a03a2b 0%, #7b2e2e 100%)',
        color: '#fff',
        padding: '3rem 2rem 2rem 2rem',
        marginTop: '4rem'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: isMobile ? '2rem' : '1rem'
        }}>
          {/* Logo Section */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: isMobile ? 'center' : 'flex-start',
            textAlign: isMobile ? 'center' : 'left'
          }}>
            <img
              src="https://pampawineclub.com/static/media/logo_resize.c8df7e69807b5ff20171.png"
              alt="Pampa Wine Club"
              style={{
                height: '60px',
                width: 'auto',
                marginBottom: '1rem',
                filter: 'brightness(0) invert(1)' // Makes logo white
              }}
            />
            <p style={{
              fontSize: '1.1rem',
              fontWeight: 600,
              margin: 0,
              letterSpacing: '0.5px'
            }}>
                              Pampa Wine Club Marketplace
            </p>
            <p style={{
              fontSize: '0.9rem',
              margin: '0.5rem 0 0 0',
              opacity: 0.9,
              fontWeight: 300
            }}>
              Los mejores vinos argentinos en un solo lugar
            </p>
          </div>

          {/* Contact Information */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: isMobile ? 'center' : 'flex-end',
            gap: '1rem',
            textAlign: isMobile ? 'center' : 'right'
          }}>
            <h4 style={{
              fontSize: '1.2rem',
              fontWeight: 700,
              margin: 0,
              marginBottom: '0.5rem'
            }}>
              Contáctanos
            </h4>
            
            {/* Email */}
            <a
              href="mailto:pampawineclub@gmail.com"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#fff',
                textDecoration: 'none',
                fontSize: '1rem',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.opacity = '0.8';
                e.target.style.transform = 'translateX(-5px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.opacity = '1';
                e.target.style.transform = 'translateX(0)';
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
              </svg>
              pampawineclub@gmail.com
            </a>

            {/* Instagram */}
            <a
              href="https://www.instagram.com/pampawineclub?igsh=MW03MGE5dXQxcDdhNg=="
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#fff',
                textDecoration: 'none',
                fontSize: '1rem',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.opacity = '0.8';
                e.target.style.transform = 'translateX(-5px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.opacity = '1';
                e.target.style.transform = 'translateX(0)';
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2m-.2 2A3.6 3.6 0 0 0 4 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6C20 5.61 18.39 4 16.4 4H7.6m9.65 1.5a1.25 1.25 0 0 1 1.25 1.25A1.25 1.25 0 0 1 17.25 8A1.25 1.25 0 0 1 16 6.75a1.25 1.25 0 0 1 1.25-1.25M12 7a5 5 0 0 1 5 5a5 5 0 0 1-5 5a5 5 0 0 1-5-5a5 5 0 0 1 5-5m0 2a3 3 0 0 0-3 3a3 3 0 0 0 3 3a3 3 0 0 0 3-3a3 3 0 0 0-3-3z"/>
              </svg>
              @pampawineclub
            </a>
          </div>
        </div>

        {/* Bottom Bar */}
        <div style={{
          borderTop: '1px solid rgba(255, 255, 255, 0.2)',
          marginTop: '2rem',
          paddingTop: '1.5rem',
          textAlign: 'center'
        }}>
          <p style={{
            margin: 0,
            fontSize: '0.9rem',
            opacity: 0.8,
            fontWeight: 300
          }}>
            © 2025 Pampa Wine Club. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default WineMarketplace; 